import React, { useEffect, useMemo, useRef, useState } from "react";
import { Empty } from "antd";
import { PlusSquareOutlined, MinusSquareOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import AppPagination from "./AppPagination";

const AppPaginatedTable = ({
  columns = [],
  data = [],
  page = 1,
  pageSize = 10,
  total = 0,
  clientSidePagination = false,
  rowOptions = [5, 10, 20, 40],
  onPageChange,
  width = "100%",
  allowedExpandableRows = [],
  rowIndexKey = "_id",
  renderExpandableRow = () => null,
  getRowClassName = () => "",
  hideScrollbar = false,
  fitWithinContainer = false,
  rowClassName = "",
  onRowClick = () => {},
  selectable = false,
  selectedRowKeys = [],
  onSelectionChange,
}) => {
  const { t } = useTranslation();

  const [currentPageSize, setCurrentPageSize] = useState(pageSize);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [currentPage, setCurrentPage] = useState(page);

  const headerCellRefs = useRef([]);
  const tableContainerRef = useRef(null);

  const [stickyOffsets, setStickyOffsets] = useState({ left: {}, right: {} });
  const [leftStickyWidth, setLeftStickyWidth] = useState(0);
  const [rightStickyWidth, setRightStickyWidth] = useState(0);

  const hasExpandableColumn = allowedExpandableRows?.length > 0;
  const hasSelectionColumn = Boolean(selectable);
  const totalColumns =
    columns.length +
    (hasExpandableColumn ? 1 : 0) +
    (hasSelectionColumn ? 1 : 0);

  const headerCheckboxRef = useRef(null);

  const leftFixedIndexes = useMemo(
    () =>
      columns.reduce((acc, col, index) => {
        if (col?.fixed === "left") acc.push(index);
        return acc;
      }, []),
    [columns]
  );

  const rightFixedIndexes = useMemo(
    () =>
      columns.reduce((acc, col, index) => {
        if (col?.fixed === "right") acc.push(index);
        return acc;
      }, []),
    [columns]
  );

  const resolveColumnWidth = (widthValue) => {
    if (widthValue === undefined || widthValue === null || widthValue === "") {
      return undefined;
    }

    const widthAsString = widthValue.toString();

    return /px|%|rem|em|vw|vh/.test(widthAsString)
      ? widthAsString
      : `${widthAsString}px`;
  };

  const getColumnWidth = (col) => {
    const resolvedWidth = resolveColumnWidth(col?.width);

    if (!resolvedWidth) {
      return {
        width: "fit-content",
        minWidth: "50px",
        maxWidth: "250px",
        flex: 1,
        flexShrink: 0,
      };
    }

    return {
      width: resolvedWidth,
      flexShrink: 0,
    };
  };

  const totalItems = clientSidePagination
    ? data?.length
    : total || data?.length;

  const totalPages = Math.max(1, Math.ceil(totalItems / currentPageSize));

  const paginatedData = useMemo(() => {
    if (!clientSidePagination) {
      return Array.isArray(data) ? data : [];
    }

    if (!Array.isArray(data) || data.length === 0) return [];

    const safePage = Math.min(Math.max(currentPage, 1), totalPages);
    const startIndex = (safePage - 1) * currentPageSize;

    return data.slice(startIndex, startIndex + currentPageSize);
  }, [data, clientSidePagination, currentPage, totalPages, currentPageSize]);

  // Selection applies to the rows currently on screen. Keys already selected on
  // other pages are left alone, so a selection can be built up across pages.
  const pageRowKeys = useMemo(
    () => paginatedData.map((row, index) => row?.[rowIndexKey] ?? index),
    [paginatedData, rowIndexKey]
  );

  const isRowSelected = (rowKey) => selectedRowKeys.includes(rowKey);

  const allPageRowsSelected =
    pageRowKeys.length > 0 && pageRowKeys.every((key) => isRowSelected(key));

  const somePageRowsSelected =
    !allPageRowsSelected && pageRowKeys.some((key) => isRowSelected(key));

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = somePageRowsSelected;
    }
  }, [somePageRowsSelected]);

  const toggleRowSelection = (rowKey) => {
    if (!onSelectionChange) return;

    onSelectionChange(
      isRowSelected(rowKey)
        ? selectedRowKeys.filter((key) => key !== rowKey)
        : [...selectedRowKeys, rowKey]
    );
  };

  const togglePageSelection = () => {
    if (!onSelectionChange) return;

    if (allPageRowsSelected) {
      onSelectionChange(
        selectedRowKeys.filter((key) => !pageRowKeys.includes(key))
      );
      return;
    }

    const additions = pageRowKeys.filter((key) => !isRowSelected(key));
    onSelectionChange([...selectedRowKeys, ...additions]);
  };

  const handleExpandRow = (rowId) => {
    setExpandedRowKeys((prev) =>
      prev.includes(rowId)
        ? prev.filter((key) => key !== rowId)
        : [...prev, rowId]
    );
  };

  const handlePageChange = (newPage) => {
    const safePage = Math.min(Math.max(newPage, 1), totalPages);

    if (clientSidePagination) {
      setCurrentPage(safePage);
      return;
    }

    onPageChange?.(safePage, currentPageSize);
  };

  const handlePageSizeChange = (newPageSize) => {
    setCurrentPageSize(newPageSize);

    if (clientSidePagination) {
      setCurrentPage(1);
      return;
    }

    const newTotalPages = Math.max(1, Math.ceil(totalItems / newPageSize));
    const newPage = Math.min(currentPage, newTotalPages) || 1;

    onPageChange?.(newPage, newPageSize);
  };

  useEffect(() => {
    setCurrentPageSize(pageSize);
  }, [pageSize]);

  useEffect(() => {
    if (clientSidePagination) {
      setCurrentPage(page);
    }
  }, [page, clientSidePagination]);

  useEffect(() => {
    const updateStickyOffsets = () => {
      const columnWidths = columns.map((col, index) => {
        const measuredWidth = headerCellRefs.current[index]?.offsetWidth;

        if (measuredWidth) return measuredWidth;

        const resolvedWidth = resolveColumnWidth(col?.width);
        const parsedWidth = Number.parseFloat(resolvedWidth || "");

        return Number.isFinite(parsedWidth) ? parsedWidth : 140;
      });

      let accumulatedLeft = 0;
      const nextLeftOffsets = {};

      leftFixedIndexes.forEach((index) => {
        nextLeftOffsets[index] = accumulatedLeft;
        accumulatedLeft += columnWidths[index] || 0;
      });

      let accumulatedRight = 0;
      const nextRightOffsets = {};

      [...rightFixedIndexes].reverse().forEach((index) => {
        nextRightOffsets[index] = accumulatedRight;
        accumulatedRight += columnWidths[index] || 0;
      });

      setStickyOffsets({
        left: nextLeftOffsets,
        right: nextRightOffsets,
      });

      setLeftStickyWidth(accumulatedLeft);
      setRightStickyWidth(accumulatedRight);
    };

    updateStickyOffsets();

    const observer =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(updateStickyOffsets)
        : null;

    if (observer) {
      headerCellRefs.current.forEach((cell) => {
        if (cell) observer.observe(cell);
      });

      if (tableContainerRef.current) {
        observer.observe(tableContainerRef.current);
      }
    }

    window.addEventListener("resize", updateStickyOffsets);

    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", updateStickyOffsets);
    };
  }, [columns, leftFixedIndexes, rightFixedIndexes]);

  const getStickyCellStyle = (col, colIndex, isHeader = false) => {
    const baseStyle = getColumnWidth(col);
    const fixedPosition = col?.fixed;

    const stickyStyle = {
      ...baseStyle,
    };

    if (fixedPosition === "left" || fixedPosition === "right") {
      stickyStyle.position = "sticky";
      stickyStyle.zIndex = isHeader ? 16 : 15;
    }

    if (fixedPosition === "left") {
      stickyStyle.left = `${stickyOffsets.left[colIndex] || 0}px`;
    }

    if (fixedPosition === "right") {
      stickyStyle.right = `${stickyOffsets.right[colIndex] || 0}px`;
    }

    return stickyStyle;
  };

  return (
    <div
      className="relative flex h-full min-h-0 min-w-0 max-w-full w-full flex-1 flex-col bg-white"
      style={{ width }}
    >
      <div
        ref={tableContainerRef}
        className="relative flex min-h-0 min-w-0 max-w-full flex-1 flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white p-2 w-full"
      >
        {data?.length > 0 && leftStickyWidth > 0 && (
          <>
            <div
              className="pointer-events-none absolute top-2 bottom-2 left-2 z-[14] bg-transparent"
              style={{ width: `${leftStickyWidth}px`, zIndex: 14, boxShadow: "8px 0 12px -8px rgba(0,0,0,0.12)", }}
            />

            <div
              className="pointer-events-none absolute top-2 bottom-2 z-[17] w-px "
              style={{ left: `${leftStickyWidth + 8}px`, zIndex: 17  }}
            />
          </>
        )}

        {data?.length > 0 && rightStickyWidth > 0 && (
          <>
            <div
              className="pointer-events-none absolute top-2 bottom-2 right-2 z-[14] bg-transparent"
              style={{ width: `${rightStickyWidth}px`, zIndex: 14, boxShadow: "-8px 0 12px -8px rgba(0,0,0,0.12)", }}
            />

            <div
              className="pointer-events-none absolute top-2 bottom-2 z-[17] w-px shadow-md"
              style={{ right: `${rightStickyWidth + 8}px`, zIndex: 17, }}
            />
          </>
        )}

        <div
          className={`min-h-0 min-w-0 max-w-full w-full flex-1 ${
            fitWithinContainer
              ? "overflow-x-hidden overflow-y-auto md:overflow-y-auto"
              : "overflow-x-auto overflow-y-auto"
          } ${hideScrollbar ? "hide-scrollbar" : ""}`}
          style={
            fitWithinContainer
              ? undefined
              : {
                  WebkitOverflowScrolling: "touch",
                  overscrollBehaviorX: "contain",
                }
          }
        >
          {data?.length > 0 ? (
            <table
              className={`w-full min-w-full border-separate ${
                fitWithinContainer ? "table-fixed" : "table-auto"
              }`}
              style={{
                width: "100%",
                ...(fitWithinContainer
                  ? { minWidth: 0 }
                  : { minWidth: "max-content" }),
                borderSpacing: "0 8px",
              }}
            >
              <colgroup>
                {hasSelectionColumn && (
                  <col style={getColumnWidth({ width: "56px" })} />
                )}

                {hasExpandableColumn && (
                  <col style={getColumnWidth({ width: "56px" })} />
                )}

                {columns?.map((col, colIndex) => (
                  <col
                    key={`table-col-${
                      col?.key || col?.dataIndex
                    }_index_${colIndex}`}
                    style={getColumnWidth(col)}
                  />
                ))}
              </colgroup>

              <thead>
                <tr className={`w-full rounded-xl transition-shadow hover:shadow-sm`}>
                  {hasSelectionColumn && (
                    <th
                      scope="col"
                      className="sticky top-0 z-10 bg-white px-4 py-3 text-left text-sm font-semibold text-gray-700"
                      style={getColumnWidth({ width: "56px" })}
                    >
                      <input
                        ref={headerCheckboxRef}
                        type="checkbox"
                        className="h-4 w-4 cursor-pointer accent-primary"
                        aria-label={t("Select all rows on this page")}
                        checked={allPageRowsSelected}
                        onChange={togglePageSelection}
                        disabled={pageRowKeys.length === 0}
                      />
                    </th>
                  )}

                  {hasExpandableColumn && (
                    <th
                      scope="col"
                      className="sticky top-0 z-10 bg-white px-4 py-3 text-left text-sm font-semibold text-gray-700"
                      style={getColumnWidth({ width: "56px" })}
                    >
                      <PlusSquareOutlined />
                    </th>
                  )}

                  {columns?.map((col, colIndex) => (
                    <th
                      key={`table-header-${
                        col?.key || col?.dataIndex
                      }_index_${colIndex}`}
                      ref={(element) => {
                        headerCellRefs.current[colIndex] = element;
                      }}
                      scope="col"
                      className={`sticky top-0 z-10 bg-white px-4 py-3 text-left text-sm font-semibold text-gray-700 ${
                        fitWithinContainer ? "min-w-0" : "whitespace-nowrap"
                      } ${col?.headerClassName || ""}`}
                      style={{
                        ...getStickyCellStyle(col, colIndex, true),
                        ...(col?.headerStyle || {}),
                      }}
                    >
                      {col?.title}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {paginatedData?.map((row, rowIndex) => {
                  const rowKey = row?.[rowIndexKey] ?? rowIndex;
                  const isExpanded = expandedRowKeys.includes(rowKey);

                  const rowCanExpand = allowedExpandableRows?.includes(
                    row?.[rowIndexKey]
                  );

                  const rowClassName = getRowClassName(row);

                  const cellBaseClasses = [
                    "bg-primary-light px-4 py-4 align-middle text-sm text-gray-700 border-t border-b border-secondary-light",
                    fitWithinContainer ? "min-w-0 overflow-hidden" : "",
                  ]
                    .filter(Boolean)
                    .join(" ");

                  const firstCellClasses =
                    "rounded-l-2xl border-l border-secondary-light";

                  const lastCellClasses =
                    "rounded-r-2xl border-r border-secondary-light";

                  return (
                    <React.Fragment
                      key={`table_row_${rowKey}_index_${rowIndex}`}
                    >
                      <tr onClick={() => onRowClick?.(row)} className={`table-row-hover w-full cursor-pointer transition-all duration-200 hover:scale-105 ${rowClassName}`}>
                        {hasSelectionColumn && (
                          <td
                            className={`${cellBaseClasses} ${firstCellClasses}`}
                            style={getColumnWidth({ width: "56px" })}
                            onClick={(event) => event.stopPropagation()}
                          >
                            <input
                              type="checkbox"
                              className="h-4 w-4 cursor-pointer accent-primary"
                              aria-label={t("Select row")}
                              checked={isRowSelected(rowKey)}
                              onChange={() => toggleRowSelection(rowKey)}
                            />
                          </td>
                        )}

                        {hasExpandableColumn && (
                          <td
                            className={`${cellBaseClasses} ${firstCellClasses} group-hover:bg-red-500`}
                            style={getColumnWidth({ width: "56px" })}
                          >
                            <button
                              type="button"
                              className="flex items-center text-sm text-gray-700"
                              disabled={!rowCanExpand}
                              onClick={() => handleExpandRow(rowKey)}
                            >
                              <span
                                className={
                                  !rowCanExpand
                                    ? "text-gray-400"
                                    : "text-current"
                                }
                              >
                                {isExpanded ? (
                                  <MinusSquareOutlined />
                                ) : (
                                  <PlusSquareOutlined />
                                )}
                              </span>
                            </button>
                          </td>
                        )}

                        {columns?.map((col, colIndex) => {
                          const isFirstDataCell =
                            !hasExpandableColumn &&
                            !hasSelectionColumn &&
                            colIndex === 0;

                          const isLastDataCell =
                            colIndex === columns.length - 1;

                          return (
                            <td
                              key={`table-row-column-${
                                col?.key || col?.dataIndex
                              }-row-${rowKey}-row-index-${rowIndex}-column-index-${colIndex}`}
                              className={[
                                cellBaseClasses,
                                isFirstDataCell ? firstCellClasses : "",
                                isLastDataCell ? lastCellClasses : "",
                              ].join(" ")}
                              style={getStickyCellStyle(col, colIndex)}
                            >
                              {col?.render
                                ? col?.render(
                                    row[col?.dataIndex],
                                    row,
                                    rowIndex
                                  )
                                : row[col?.dataIndex]}
                            </td>
                          );
                        })}
                      </tr>

                      {isExpanded && (
                        <tr>
                          <td colSpan={totalColumns} className="pt-0">
                            <div className="rounded-b-2xl bg-primary-light px-4 pb-4 pt-2 text-sm text-gray-700">
                              {renderExpandableRow(row)}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}

                <tr className="w-full">
                  <td className="p-6" />
                </tr>
              </tbody>
            </table>
          ) : (
            <div className="py-2 flex-1 h-full flex justify-center items-center">
              <Empty description={t("No data found")} />
            </div>
          )}
        </div>
      </div>

      {totalPages > 1 && (
        <div
          className="sticky z-10 mt-auto min-w-0 max-w-full shrink-0 bg-white px-3 py-3 sm:px-6 sm:py-4"
          style={{ bottom: "-10px", zIndex: 17 }}
        >
          <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
            <div className="flex min-w-0 flex-wrap items-center gap-2 text-sm text-gray-600">
              <span>{t("Show")}</span>

              <select
                value={currentPageSize}
                onChange={(e) =>
                  handlePageSizeChange(Number(e.target.value))
                }
                className="min-w-0 rounded-md border border-gray-300 bg-white px-2 py-1 text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                {rowOptions.map((opt) => (
                  <option key={`table-option-${opt}`} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>

              <span className="whitespace-nowrap">
                {t("of")} {totalItems} {t("entries")}
              </span>
            </div>

            <div className="flex min-w-0 w-full justify-center sm:w-auto sm:justify-end">
              <AppPagination
                currentPage={Math.min(
                  Math.max(clientSidePagination ? currentPage : page, 1),
                  totalPages
                )}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppPaginatedTable;